import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { ShowHeader } from '@/common/components/ShowHeader'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDate } from '@/common/utils/formatters'
import useBillingBill from '@/features/billingBills/hooks/useBillingBill'
import useEstablishmentCompany from '@/features/establishments/hooks/useEstablishmentCompany'
import { PaymentAdvanceConfirmModal } from '@/features/paymentAdvances/components/PaymentAdvanceConfirmModal'
import { PaymentAdvanceRejectModal } from '@/features/paymentAdvances/components/PaymentAdvanceRejectModal'
import { ResumeItem } from '@/features/paymentAdvances/components/ResumeItem'
import { usePaymentAdvance } from '@/features/paymentAdvances/hooks/usePaymentAdvance'
import { useReviewPaymentAdvance } from '@/features/paymentAdvances/hooks/useReviewPaymentAdvance'
import { useQueryClient } from '@tanstack/react-query'
import { BadgeAlertIcon, BadgeCheckIcon, BadgeXIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const PaymentAdvanceShow = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false)
  const [confirmMessageType, setConfirmMessageType] = useState<'approve' | 'reject'>()

  const {
    data: paymentAdvance,
    isPending: paymentAdvanceIsPending,
    error: paymentAdvanceError = {} as any,
    refetch: paymentAdvanceRefetch,
  } = usePaymentAdvance(id || '')

  const { data: bill } = useBillingBill({ id: paymentAdvance?.bill_id, enabled: !!paymentAdvance?.bill_id })

  const { data: establishment } = useEstablishmentCompany(
    paymentAdvance?.establishment_company?.id || '',
    !!paymentAdvance?.establishment_company?.id,
  )

  const { mutate: reviewPaymentAdvance, isPending: reviewPaymentAdvanceIsPending } = useReviewPaymentAdvance({
    onSuccess: () => {
      setConfirmModalIsOpen(true)
    },
    onError: (error) => {
      const { errors } = error?.response?.data as { errors: Record<string, string[]> }

      if (!errors || !Object.keys(errors).length) {
        return
      }

      if (errors?.base?.length && setMessages) {
        setMessages(errors.base)
      }
    },
  })

  const onConfirm = () => {
    setConfirmModalIsOpen(false)
    queryClient.invalidateQueries({ queryKey: ['paymentAdvance', id] })
    queryClient.invalidateQueries({ queryKey: ['paymentAdvances'] })
    navigate('/payment-advances')
  }

  if (paymentAdvanceIsPending) return <Box>Carregando...</Box>

  return (
    <div>
      <ShowHeader
        goBack
        title="Antecipar recebíveis"
        buttons={
          <>
            {paymentAdvance?.status === 'pending' && (
              <div className="flex gap-x-2">
                <PaymentAdvanceRejectModal
                  paymentAdvanceId={paymentAdvance.id}
                  disabled={reviewPaymentAdvanceIsPending}
                />

                <Button
                  onClick={() => {
                    setConfirmMessageType('approve')
                    reviewPaymentAdvance({ id: paymentAdvance?.id || '', state: 'approve' })
                  }}
                  disabled={reviewPaymentAdvanceIsPending}
                >
                  Aprovar
                </Button>
              </div>
            )}
          </>
        }
      />

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={paymentAdvanceError?.status}
        refetchFunction={paymentAdvanceRefetch}
      >
        <Box className="max-h-[calc((100vh-104px-1.5rem))] overflow-y-auto space-y-6">
          <div className="space-y-4">
            {paymentAdvance?.status === 'pending' && (
              <div className="flex items-center gap-1 text-warning">
                <BadgeAlertIcon strokeWidth={1} />
                <span className="font-bold">Adiantamento pendente</span>
              </div>
            )}

            {paymentAdvance?.status === 'approved' && (
              <div className="flex items-center gap-1 text-success">
                <BadgeCheckIcon strokeWidth={1} />
                <span className="font-bold">Adiantamento aprovado</span>
              </div>
            )}

            {paymentAdvance?.status === 'rejected' && (
              <div className="flex items-center gap-1 text-danger">
                <BadgeXIcon strokeWidth={1} />
                <span className="font-bold">Adiantamento rejeitado</span>
              </div>
            )}

            {paymentAdvance?.reject_reason && (
              <div className="w-full h-20 bg-[#FCCDCA33] rounded-lg overflow-y-auto p-4">
                <p className="text-white-800 text-sm">{paymentAdvance.reject_reason}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">

            <p>Detalhes da fatura </p>

            <div className="flex gap-4">
              <div>
                <p className="text-sm font-light italic">Fatura {bill?.number}</p>

                <p className="text-lg font-bold">
                  {bill?.opened_at ? formatDate(new Date(bill?.opened_at)) : ''} -{' '}
                  {bill?.closed_at ? formatDate(new Date(bill?.closed_at)) : ''}
                </p>
              </div>

              <div>
                <p className="text-sm font-light italic">Saldo da fatura</p>

                <p className="text-lg font-bold">{formatCurrency(Number(bill?.total_amount || 0))}</p>
              </div>

              <div>
                <p className="text-sm font-light italic">Data para recebimento</p>

                <p className="text-lg font-bold">{bill?.due_at ? formatDate(new Date(bill.due_at)) : '-'}</p>
              </div>
            </div>
          </div>

          <div className="grid w-full grid-cols-2 gap-4">
            <ResumeItem
              label="Valor solicitado para antecipação"
              value={formatCurrency(Number(paymentAdvance?.discounted_from_bill ?? 0))}
            />

            <ResumeItem
              label="Data para recebimento"
              value={paymentAdvance?.date_to_be_paid ? dateTransformer(paymentAdvance?.date_to_be_paid) : ''}
            />
          </div>

          <div className="space-y-2">
            <p>Cálculo de juros</p>

            <ResumeItem label="Juros ao dia" value={`${Number(paymentAdvance?.applied_daily_interest ?? 0) * 100}%`} />
          </div>

          <div className="w-full h-px bg-white-300" />

          <div className="grid grid-cols-2 gap-4 items-start">
            <ResumeItem
              label="Valor a receber antecipado"
              value={formatCurrency(Number(paymentAdvance?.total_to_be_paid ?? 0))}
            />

            <ResumeItem
              label="Valor a pagar de juros"
              value={`${formatCurrency(Number(paymentAdvance?.interest_total ?? 0))} (${Number(paymentAdvance?.interest_percentage ?? 0) * 100
                }%)`}
              className="bg-yellow-op-2"
            />
          </div>

          <div className="grid w-full grid-cols-2 gap-4">
            <ResumeItem
              label="Valor a ser abatido da fatura"
              value={formatCurrency(Number(paymentAdvance?.discounted_from_bill ?? 0))}
            />
          </div>

          {establishment?.matrix_branch?.bank_account_information && (
            <Box className="space-y-4 p-4">
              <p>Detalhes bancários</p>

              <div className="grid w-full grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-light italic">Nome do banco</p>

                  <p className="text-lg font-bold">
                    {establishment?.matrix_branch?.bank_account_information?.bank_institution?.name}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Número do banco</p>

                  <p className="text-lg font-bold">
                    {establishment?.matrix_branch?.bank_account_information?.bank_institution?.code}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Agência (com dígito)</p>

                  <p className="text-lg font-bold">{establishment?.matrix_branch?.bank_account_information?.agency}</p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Conta (com dígito)</p>

                  <p className="text-lg font-bold">{establishment?.matrix_branch?.bank_account_information?.account}</p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Favorecido</p>

                  <p className="text-lg font-bold">
                    {establishment?.matrix_branch?.bank_account_information?.payee_name}
                  </p>
                </div>
              </div>
            </Box>
          )}
        </Box>
      </ErrorMessageWrapper>

      <PaymentAdvanceConfirmModal
        isOpen={confirmModalIsOpen}
        setIsOpen={setConfirmModalIsOpen}
        message={confirmMessageType === 'approve' ? 'Adiantamento aprovado!' : 'Adiantamento rejeitado!'}
        showEstablishment={confirmMessageType === 'approve'}
        establishmentName={establishment?.general_information?.fantasy_name}
        establishmentLogoUrl={establishment?.general_information?.company_logo?.url}
        showResume={confirmMessageType === 'approve'}
        totalToBePaid={paymentAdvance?.total_to_be_paid}
        dateToBePaid={paymentAdvance?.date_to_be_paid ? dateTransformer(paymentAdvance?.date_to_be_paid) : ''}
        onConfirm={onConfirm}
      />

      {messages && <ModalMessageSimple messages={messages as string[]} setMessages={setMessages} isError />}
    </div>
  )
}

export default PaymentAdvanceShow
